import type { VideoType } from '@app-types/video'
import { Anchor } from '@components/anchor'
import type { ContentfulImageData } from '@components/contentful-image'
import { ContentfulImage } from '@components/contentful-image'
import { ExternalLink } from '@components/icons/external-link'
import { Skeleton } from '@components/skeleton'
import { Text } from '@components/text'
import { Video } from '@components/video'
import type { ThumbnailFragment } from '@graphql/fragments/ThumbnailFragment.graphql'
import { iconSizeMedium } from '@styles/variables'
import classNames from 'classnames'
import type { FunctionComponent, HTMLAttributes } from 'react'
import { Fragment } from 'react'
import type { LiteralUnion } from 'type-fest'

import styles from './thumbnail.module.scss'

/** Renders image and caption for whatever you want a preview of. */
export const Thumbnail: FunctionComponent<React.PropsWithChildren<Props>> = ({
    type,
    image,
    video,
    subtitle,
    title,
    href,
    target,
    fit = 'cover',
    size = 'medium',
    openInNewWindow = false,
    priority = false,
    ...restOfProps
}) => {
    const [Wrapper, wrapperProps] = href
        ? [Anchor, { href, target, preset: 'plain' as const }]
        : [Fragment, {} as any]

    return (
        <Wrapper {...wrapperProps}>
            <figure
                tabIndex={href ? 0 : -1}
                aria-label={[title, subtitle].filter(Boolean).join(': ')}
                className={classNames(
                    styles.figure,
                    styles[size],
                    styles[`${size}-${fit}`],
                    { [styles.interactive]: href },
                )}
                {...restOfProps}
            >
                <div className={classNames(styles['image-wrapper'])}>
                    <Preview
                        video={video}
                        image={image}
                        size={size}
                        fit={fit}
                        priority={priority}
                    />

                    {href && href?.indexOf('video') > -1 && (
                        <div className={styles.playBtnContainer}>
                            <img
                                src='/images/play-button.webp'
                                alt='Play video'
                                width={100}
                                height={100}
                            />
                        </div>
                    )}

                    {subtitle && type !== 'Icon' && (
                        <div className={styles.tagContainer}>
                            <div
                                className={`${styles.subtitle} ${
                                    href && href?.indexOf('video') > -1
                                        ? styles.watch
                                        : styles.read
                                }`}
                            >
                                <Text tag='div' typeStyle='body-md'>
                                    {href && href?.indexOf('video') > -1
                                        ? 'Watch'
                                        : 'Read'}
                                </Text>
                            </div>

                            <div
                                className={classNames(
                                    styles.subtitle,
                                    styles[`subtitle-color-future-fuscia`],
                                )}
                            >
                                <Text
                                    tag='div'
                                    typeStyle='body-md'
                                    color='midnight'
                                >
                                    {subtitle}
                                </Text>
                            </div>
                        </div>
                    )}
                </div>

                {title && (
                    <figcaption className={styles[`caption-${size}`]}>
                        <div className={styles.title}>
                            <Text typeStyle='body-md' tag='span'>
                                {title}
                            </Text>
                            {openInNewWindow && <ExternalLink />}
                        </div>
                    </figcaption>
                )}

                {subtitle && type === 'Icon' && (
                    <Text tag='div' typeStyle='body-sm'>
                        {subtitle}
                    </Text>
                )}
            </figure>
        </Wrapper>
    )
}

const Preview: FunctionComponent<
    React.PropsWithChildren<
        Pick<Props, 'size' | 'video' | 'image' | 'fit' | 'priority'>
    >
> = ({ size, image, video, fit, priority }) => {
    const width = size === 'medium' ? '100%' : iconSizeMedium

    const className = classNames(
        styles.image,
        styles[`image-${size}`],
        styles[`image-${size}-${fit}`],
    )

    if (video) {
        return (
            <Video
                {...video}
                className={className}
                preset={video.preset !== 'gif' ? 'video' : video.preset}
                width={width}
                sources={video.sources}
                poster={video.poster}
            />
        )
    }

    if (image) {
        return (
            <ContentfulImage
                url={image.url}
                alt={image.alt}
                contentType={image.contentType}
                sizes={
                    size === 'medium'
                        ? '(max-width: 667px) 90vw, (max-width: 1024px) 45vw, 30vw'
                        : '60px'
                }
                priority={priority}
                className={className}
                style={{ objectFit: fit }}
            />
        )
    }

    return <Skeleton className={styles['image-skeleton']} width={width} />
}

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
    /** Is the content inside loading */
    loading?: boolean
    /** Image to display */
    image?: ContentfulImageData
    /** Image to display */
    video?: VideoType
    /** Non-bold text above the title. */
    subtitle?: string
    /** Bold text below the image. */
    title?: string
    /** If this is passed, an anchor will be rendered. */
    href?: string
    /** Anchor target. */
    target?: string
    /** How the image fills its container.  */
    fit?: 'cover' | 'contain'
    /** Affects the size of the image and width of the container.  */
    size?: LiteralUnion<'small' | 'medium', string>
    /** If true, the link will open in a new window. */
    openInNewWindow?: boolean
    /** The type of thumbnail. */
    type?: ThumbnailFragment['type']
    /** Marks image as above-the-fold for LCP optimization */
    priority?: boolean
}
